import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { Button, Icon, Modal } from "@/ui";
import { moveItem } from "./form";

type Props<T> = Base<T> & (
  /** 可排序时必须给本行称呼（如"第 2 条"），拼进按钮的无障碍名称："上移第 2 条"。 */
  | { movable?: true; label: string }
  /** 顺序由别的字段决定（例如通道组成员按优先级）时传 false，只留删除；label 仍可给删除按钮用。 */
  | { movable: false; label?: string }
);

interface Base<T> {
  rows: T[];
  index: number;
  onChange: (rows: T[]) => void;
  /** 至少保留一行时传 1：只剩这么多行时删除按钮禁用。 */
  keep?: number;
  /** 不提供删除时传 false，只留上移、下移。 */
  removable?: boolean;
  /** 删除按钮的动词，无障碍名称为"动词 + label"：只解除关系时写"移除"。 */
  removeVerb?: string;
  /** 删除本行；默认从 rows 中滤掉本行。调用方需要在删除后移交焦点时自己处理。 */
  onRemove?: () => void;
  /** 只读或保存中时整组禁用。 */
  disabled?: boolean;
  /** 删除前先确认：返回对话框的标题与正文；点"移除/删除"才真正删除，取消或 Esc 不删除、焦点回到删除按钮。返回 undefined（例如还没填内容的空行）时直接删除。 */
  confirmRemove?: () => { title: ReactNode; body: ReactNode } | undefined;
}

/** 删除确认对话框：取消在左并获得焦点，危险样式的确认按钮写动作本身。 */
function RemoveConfirm({ verb, content, onConfirm, onClose }: { verb: string; content: { title: ReactNode; body: ReactNode }; onConfirm: () => void; onClose: () => void }) {
  return <Modal open title={content.title} onClose={onClose}
    footer={<><Button data-autofocus onClick={onClose}>取消</Button><Button variant="danger" onClick={onConfirm}>{verb}</Button></>}>
    <p className="ax-secondary" style={{ margin: 0 }}>{content.body}</p>
  </Modal>;
}

/**
 * 列表中一项的上移、下移与删除，都是小号图标幽灵按钮：通道组成员与账号路由绑定共用。第一项不能上移、最后一项不能下移。
 * 删除用垃圾桶图标加 is-danger（悬停或聚焦变危险色），带 data-remove-row 供调用方在删除后定位相邻行的删除按钮；
 * 给 confirmRemove 时先弹确认对话框（设计系统 Modal），确认后才删除。
 * 移动后整行在 DOM 里重排，浏览器会丢掉焦点：在新顺序提交后的布局阶段把焦点放回被点的箭头；
 * 移到首位或末位时被点的箭头变成禁用，改交给同一行的另一个箭头。
 */
export function RowActions<T>({ rows, index, onChange, keep = 0, movable = true, removable = true, removeVerb = "删除", onRemove, confirmRemove, disabled = false, label = "" }: Props<T>) {
  const up = useRef<HTMLButtonElement>(null), down = useRef<HTMLButtonElement>(null);
  const handoff = useRef<HTMLButtonElement | null>(null);
  const [confirming, setConfirming] = useState<{ title: ReactNode; body: ReactNode } | null>(null);
  const remove = onRemove ?? (() => onChange(rows.filter((_, j) => j !== index)));
  useLayoutEffect(() => { handoff.current?.focus();handoff.current = null; }, [index]);
  const move = (dir: -1 | 1) => {
    const to = index + dir, atEdge = to === 0 || to === rows.length - 1;
    handoff.current = (dir === -1) !== atEdge ? up.current : down.current;
    onChange(moveItem(rows, index, dir));
  };
  return (
    <>
      {movable && <Button ref={up} variant="ghost" size="sm" iconOnly aria-label={`上移${label}`} title="上移" disabled={disabled || index === 0} onClick={() => move(-1)}><Icon><path d="M12 19V5" /><path d="m5 12 7-7 7 7" /></Icon></Button>}
      {movable && <Button ref={down} variant="ghost" size="sm" iconOnly aria-label={`下移${label}`} title="下移" disabled={disabled || index === rows.length - 1} onClick={() => move(1)}><Icon><path d="M12 5v14" /><path d="m19 12-7 7-7-7" /></Icon></Button>}
      {removable && <Button variant="ghost" size="sm" iconOnly className="is-danger" data-remove-row aria-label={`${removeVerb}${label}`} title={removeVerb} disabled={disabled || rows.length <= keep}
        onClick={() => { const c = confirmRemove?.();if (c) setConfirming(c);else remove(); }}><Icon><path d="M3 6h18" /><path d="M8 6V4h8v2" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /></Icon></Button>}
      {confirming && <RemoveConfirm verb={removeVerb} content={confirming} onClose={() => setConfirming(null)} onConfirm={() => { setConfirming(null);remove(); }} />}
    </>
  );
}
